// src/lib/issue-pill.ts
// Pastille d'issue d'un RDV sur la ligne d'Aujourd'hui (closé / à relancer / perdu). Module pur.
import type { ProductKey, Prospect, RdvIssue } from "@shared/schema";
import { PRODUCTS } from "@shared/products";
import { dayMonth } from "./brief";

/** « jeu. » pour un jour AAAA-MM-JJ. Calcul en UTC : le jour ne dépend pas du fuseau du navigateur. */
function shortWeekday(isoDate: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate);
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12));
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString("fr-FR", { timeZone: "UTC", weekday: "short" });
}

/** Heure posée par la modale d'issue dans `nextAction` (« Relance à 14h » / « Relance à 14h30 »). */
function relanceHour(nextAction: string | null | undefined): string | null {
  const m = /^Relance à (\d{1,2}h(?:\d{2})?)$/.exec((nextAction ?? "").trim());
  return m ? m[1] : null;
}

/** « jeu. 06/11 14h », « jeu. 06/11 », ou null sans date de relance. */
export function relanceWhen(
  nextFollowUpAt: string | null | undefined,
  nextAction: string | null | undefined,
): string | null {
  const day = dayMonth(nextFollowUpAt);
  if (!day) return null;
  const weekday = shortWeekday(nextFollowUpAt ?? "");
  const hour = relanceHour(nextAction);
  return [weekday, day, hour].filter(Boolean).join(" ");
}

/** Texte de la pastille d'issue. */
export function issuePillLabel(
  issue: RdvIssue,
  prospect: Pick<Prospect, "nextFollowUpAt" | "nextAction" | "lossReason">,
  lastSaleProduct: ProductKey | null,
): string {
  switch (issue) {
    case "closed": {
      const product = lastSaleProduct ? PRODUCTS[lastSaleProduct]?.shortLabel : null;
      return product ? `✓ Closé · ${product}` : "✓ Closé";
    }
    case "relance": {
      const when = relanceWhen(prospect.nextFollowUpAt, prospect.nextAction);
      return when ? `À relancer · ${when}` : "À relancer";
    }
    case "lost": {
      const reason = prospect.lossReason?.trim();
      return reason ? `Perdu · ${reason}` : "Perdu";
    }
  }
}
